import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { scheduleKeys } from './queryKeys';

// Types for swap data
export interface SwapRequest {
  swap_id: string;
  status: string;
  message: string;
  created_at: string;
  expires_at: string;
  requester_shift_start: string;
  requester_shift_end: string;
  candidate_count: number;
  accepted_candidate_name: string;
}

export interface SwapOffer {
  swap_id: string;
  candidate_id: string;
  candidate_status: string;
  requester_name: string;
  message: string;
  created_at: string;
  expires_at: string;
  requester_shift_start: string;
  requester_shift_end: string;
  my_shift_start: string;
  my_shift_end: string;
}

export interface EligibleCandidate {
  profile_id: string;
  profile_name: string;
  shift_id: string;
  shift_start_time: string;
  shift_end_time: string;
  shift_note: string;
}

/**
 * Hook to fetch user's outgoing swap requests
 */
export const useMySwapRequests = (statusFilter?: string[]) => {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: scheduleKeys.mySwapRequests(profile?.id || '', statusFilter),
    queryFn: async (): Promise<SwapRequest[]> => {
      const { data, error } = statusFilter 
        ? await supabase.rpc('list_my_swap_requests_filtered', { p_status_filter: statusFilter })
        : await supabase.rpc('list_my_swap_requests');
      
      if (error) {
        console.error('[Frontend] Error fetching my swap requests:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!profile?.id,
    staleTime: 30 * 1000, // ✅ FIXED: Reduced to 30 seconds to prevent stale data
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30 * 60 * 1000, // 30 minutes - reduced from 60 seconds to save costs
  });
};

/**
 * Hook to fetch incoming swap offers for the current user
 */
export const useSwapOffersForMe = (statusFilter?: string[]) => {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: scheduleKeys.swapOffersForMe(profile?.id || '', statusFilter),
    queryFn: async (): Promise<SwapOffer[]> => {
      const { data, error } = statusFilter 
        ? await supabase.rpc('list_swap_offers_for_me_filtered', { p_status_filter: statusFilter })
        : await supabase.rpc('list_swap_offers_for_me');
      
      if (error) {
        console.error('[Frontend] Error fetching swap offers for me:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!profile?.id,
    staleTime: 30 * 1000, // ✅ FIXED: Reduced to 30 seconds to prevent stale data
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30 * 60 * 1000, // 30 minutes - reduced from 60 seconds to save costs
  });
};

/**
 * Hook to fetch eligible candidates for swapping with a specific shift
 */
export const useEligibleSwapCandidates = (shiftId: string | undefined) => {
  return useQuery({
    queryKey: scheduleKeys.eligibleSwapCandidates(shiftId || ''),
    queryFn: async (): Promise<EligibleCandidate[]> => {
      if (!shiftId) throw new Error('Shift ID required');
      
      const { data, error } = await supabase.rpc('get_eligible_swap_candidates', {
        p_shift_id: shiftId
      });
      
      if (error) {
        console.error('[Frontend] Error fetching eligible swap candidates:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!shiftId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on focus for candidate lists
    refetchOnReconnect: true,
  });
};
